// frontend/src/components/WhatToDoBanner.tsx
//
// «Что делать сейчас» — анализирует текущее состояние всех гипотез и
// показывает один большой call-to-action на верху страницы /hypotheses.
//
// Цель: вместо того чтобы Денис разбирался в 7 статусах state machine,
// он видит «прямо сейчас 10 черновиков ждут одобрения, открой первый».

import { Link } from "react-router-dom";
import { ChevronRight, AlertCircle, CheckCircle, Play, Trophy, Plus } from "lucide-react";
import type { Hypothesis } from "../api/hypotheses.js";

interface Props {
  entries: Hypothesis[] | null;
}

function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

interface Action {
  icon: typeof AlertCircle;
  color: string;
  bg: string;
  text: string;
  sub: string;
  href: string;
  cta: string;
}

export function WhatToDoBanner({ entries }: Props) {
  if (!entries || entries.length === 0) {
    return (
      <Banner action={{
        icon: Plus,
        color: "#0EA5E9",
        bg: "rgba(14,165,233,0.08)",
        text: "Гипотез пока нет",
        sub: "Создай первую узкую тест-кампанию: 1 товар × 1 аудитория × 1 посыл",
        href: "/hypotheses/new",
        cta: "+ Создать первую",
      }} />
    );
  }

  const drafts = entries.filter((h) => h.status === "draft");
  const approved = entries.filter((h) => h.status === "approved");
  const active = entries.filter((h) => h.status === "active");
  const verdictPending = entries.filter((h) => h.status === "verdict_pending");
  const paused = entries.filter((h) => h.status === "paused");

  // Приоритет: verdict_pending > approved > drafts > active > paused
  // Идея: первое что требует решения — приоритет 1.

  let primary: Action;

  if (verdictPending.length > 0) {
    primary = {
      icon: Trophy,
      color: "#F59E0B",
      bg: "rgba(245,158,11,0.08)",
      text: `${verdictPending.length} ${plural(verdictPending.length, ["гипотеза получила вердикт", "гипотезы получили вердикт", "гипотез получили вердикт"])} — нужно решение`,
      sub: "Pass → масштабируем (×3 бюджет). Fail → убиваем. Inconclusive → продлеваем.",
      href: `/hypotheses/${verdictPending[0].id}`,
      cta: "Открыть первую",
    };
  } else if (approved.length > 0) {
    primary = {
      icon: Play,
      color: "#10B981",
      bg: "rgba(16,185,129,0.08)",
      text: `${approved.length} ${plural(approved.length, ["гипотеза одобрена", "гипотезы одобрены", "гипотез одобрены"])} — пора запустить в Я.Директ`,
      sub: "Открой каждую, проверь dry-run preview, нажми «Launch». Система сама создаст кампанию.",
      href: `/hypotheses/${approved[0].id}`,
      cta: "Запустить первую",
    };
  } else if (drafts.length > 0) {
    primary = {
      icon: CheckCircle,
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.08)",
      text: `${drafts.length} ${plural(drafts.length, ["черновик готов", "черновика готовы", "черновиков готовы"])} к одобрению`,
      sub: "Открой каждый, проверь лендинг и текст, нажми «Одобрить». Потом можно будет запустить.",
      href: `/hypotheses/${drafts[0].id}`,
      cta: "Открыть первый",
    };
  } else if (active.length > 0) {
    primary = {
      icon: Play,
      color: "#10B981",
      bg: "rgba(16,185,129,0.08)",
      text: `${active.length} ${plural(active.length, ["кампания работает", "кампании работают", "кампаний работают"])} прямо сейчас`,
      sub: "Cron сам поставит вердикт через 60 кликов или 14 дней. Можно смотреть результаты.",
      href: "/",
      cta: "Открыть дашборд",
    };
  } else if (paused.length > 0) {
    primary = {
      icon: AlertCircle,
      color: "#F59E0B",
      bg: "rgba(245,158,11,0.08)",
      text: `${paused.length} ${plural(paused.length, ["гипотеза на паузе", "гипотезы на паузе", "гипотез на паузе"])} — что с ними?`,
      sub: "Resume → запустить заново. Kill → закрыть окончательно.",
      href: `/hypotheses/${paused[0].id}`,
      cta: "Открыть первую",
    };
  } else {
    primary = {
      icon: Plus,
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.08)",
      text: "Ничего активного — старт следующей волны",
      sub: "Создай новую узкую кампанию или регенерируй из топ-SKU",
      href: "/hypotheses/new",
      cta: "+ Создать",
    };
  }

  // Counts panel — все статусы для контекста
  const counts = [
    { label: "Черновики", n: drafts.length, color: "#64748B" },
    { label: "Одобрены", n: approved.length, color: "#10B981" },
    { label: "Работают", n: active.length, color: "#0EA5E9" },
    { label: "Вердикт", n: verdictPending.length, color: "#F59E0B" },
    { label: "На паузе", n: paused.length, color: "#94A3B8" },
  ];

  return (
    <div style={{ marginBottom: 20 }}>
      <Banner action={primary} />
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          flexWrap: "wrap",
        }}
      >
        {counts.map((c) => (
          <div
            key={c.label}
            style={{
              padding: "6px 12px",
              background: "var(--surface-card)",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              fontSize: 12,
              display: "flex",
              gap: 6,
              alignItems: "center",
              color: c.n > 0 ? "var(--text-primary)" : "var(--text-muted)",
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: "50%",
                background: c.n > 0 ? c.color : "transparent",
                border: c.n > 0 ? "none" : `1px solid ${c.color}`,
              }}
            />
            <span>{c.label}:</span>
            <strong style={{ color: c.n > 0 ? c.color : "var(--text-muted)" }}>{c.n}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function Banner({ action }: { action: Action }) {
  const Icon = action.icon;
  return (
    <div
      style={{
        background: action.bg,
        border: `1px solid ${action.color}33`,
        borderRadius: 10,
        padding: 16,
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 44, height: 44, borderRadius: 10,
          background: action.color,
          color: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          boxShadow: `0 4px 12px ${action.color}55`,
        }}
      >
        <Icon size={22} strokeWidth={2.25} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            color: action.color,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: 2,
          }}
        >
          Что делать сейчас
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.3 }}>
          {action.text}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
          {action.sub}
        </div>
      </div>
      <Link
        to={action.href}
        style={{
          padding: "10px 16px",
          background: action.color,
          color: "#FFFFFF",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        {action.cta} <ChevronRight size={14} strokeWidth={2.5} />
      </Link>
    </div>
  );
}
